import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HeartHandshake, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api, fileUrl } from '../lib/api';

export default function Supporters() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/supporter-wall').then((r) => { setData(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return <div className="max-w-5xl mx-auto px-4 py-16"><div className="h-8 w-64 bg-slate-100 rounded animate-pulse" /></div>;
  }

  if (!data?.published) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="supporters-unavailable">
        <HeartHandshake className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">This page isn't available yet</h1>
        <p className="text-sm text-slate-500 mt-2">Check back soon.</p>
        <Button asChild className="mt-5 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="supporters-home-btn">
          <Link to="/home">Back to Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="supporters-page">
      <div className="text-left max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-widest text-[#7A5C12]">Ward 8 Voices</p>
        <h1 className="font-serif font-black text-3xl md:text-4xl text-[#1a2744] mt-2" data-testid="supporters-title">{data.title}</h1>
        <p className="text-slate-600 mt-3" data-testid="supporters-subtitle">{data.subtitle}</p>
      </div>

      {data.supporters.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center mt-8" data-testid="supporters-none">
          <p className="text-sm font-bold text-[#1a2744]">Supporter statements will appear here as residents add their names.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="supporters-grid">
          {data.supporters.map((s) => (
            <Card key={s.id} className={s.featured ? 'border-[#C9A84C]/60' : ''} data-testid={`public-supporter-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  {s.photo ? (
                    <img src={fileUrl(s.photo)} alt={s.display_name} className="h-12 w-12 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-[#1a2744]/5 flex items-center justify-center text-[#1a2744] font-serif font-bold">{s.display_name[0]}</div>
                  )}
                  <div>
                    <p className="font-serif font-bold text-[#1a2744]">{s.display_name}</p>
                    {s.area && <p className="text-xs text-slate-500">{s.area}</p>}
                  </div>
                  {s.featured && <Star className="h-4 w-4 text-[#C9A84C] ml-auto shrink-0" />}
                </div>
                {s.statement && <p className="text-sm text-slate-600 mt-3 italic">“{s.statement}”</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400 mt-8" data-testid="supporters-disclaimer">{data.disclaimer}</p>
    </div>
  );
}
